import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Mic, MicOff, Headphones, HeadphoneOff, Hand, PhoneOff, Users, Radio } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";

const MeetingRoom = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const [deafened, setDeafened] = useState(false);
  const [handRaised, setHandRaised] = useState(false);

  const load = async () => {
    try {
      await api.post(`/meetings/${id}/join`).catch(() => {});
      const { data } = await api.get(`/meetings/${id}`);
      setMeeting(data.meeting || data);
    } catch (e) { toast.error("Could not load room"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const iv = setInterval(load, 8000); return () => clearInterval(iv); /* eslint-disable-next-line */ }, [id]);

  const raiseHand = () => {
    const n = !handRaised; setHandRaised(n);
    toast(n ? "✋ Your hand is raised" : "Hand lowered", { description: n ? "Everyone can see you'd like to speak" : undefined });
  };

  const leave = () => navigate("/meetings");

  const participants = meeting?.participants || [];
  const selfName = user?.display_alias || user?.name || "You";
  const grid = [{ id: "self", name: selfName, self: true, avatar: user?.avatar_base64 }, ...participants.map((p, i) => ({
    id: p.user_id || p.id || i, name: p.anonymous_name || p.display_name || p.name || "Member", avatar: p.avatar_base64,
  }))];

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="min-h-screen flex flex-col bg-background sea-gradient">
      <header className="h-16 border-b border-border flex items-center justify-between px-4 sm:px-6 bg-card/60 backdrop-blur">
        <div className="flex items-center gap-2">
          <Radio className="w-5 h-5 text-primary" />
          <div>
            <p className="font-heading font-semibold leading-tight" data-testid="room-title">{meeting?.title || "Recovery Room"}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" /> {grid.length} present</p>
          </div>
        </div>
        <span className="text-xs px-3 py-1 rounded-full bg-seafoam/15 text-seafoam font-medium">Live</span>
      </header>

      <div className="flex-1 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6 text-center text-sm text-muted-foreground bg-secondary/40 border border-border rounded-xl px-4 py-3">
            🎧 Live voice is coming in a later phase. This is the room space — controls below are ready for when audio goes live.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="participant-grid">
            {grid.map((p) => (
              <div key={p.id} className="bg-card border border-border rounded-2xl p-5 flex flex-col items-center gap-3 relative">
                {p.self && handRaised && <span className="absolute top-2 right-2 text-warning"><Hand className="w-5 h-5" /></span>}
                <Avatar name={p.name} src={p.avatar} size={64} ring={p.self} />
                <p className="text-sm font-medium truncate max-w-full">{p.name}{p.self ? " (you)" : ""}</p>
                {p.self && muted && <MicOff className="w-4 h-4 text-muted-foreground" />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <footer className="border-t border-border bg-card/70 backdrop-blur p-4">
        <div className="max-w-md mx-auto flex items-center justify-center gap-3">
          <button data-testid="mute-toggle" onClick={() => setMuted((m) => !m)}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${muted ? "bg-secondary text-muted-foreground" : "bg-primary text-primary-foreground"}`}>
            {muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
          <button data-testid="deafen-toggle" onClick={() => setDeafened((d) => !d)}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${deafened ? "bg-secondary text-muted-foreground" : "bg-secondary text-foreground"}`}>
            {deafened ? <HeadphoneOff className="w-5 h-5" /> : <Headphones className="w-5 h-5" />}
          </button>
          <button data-testid="raise-hand" onClick={raiseHand}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${handRaised ? "bg-warning text-nightsea" : "bg-secondary text-foreground"}`}>
            <Hand className="w-5 h-5" />
          </button>
          <button data-testid="leave-room" onClick={leave} className="w-12 h-12 rounded-full bg-destructive text-white flex items-center justify-center hover:opacity-90">
            <PhoneOff className="w-5 h-5" />
          </button>
        </div>
      </footer>
    </div>
  );
};

export default MeetingRoom;
